import React from "react"

import styled from "@emotion/styled"

import {
  ContentWrapper,
  H1,
  H2,
  Layout,
  P,
  shevy,
  toEm,
  Wrapper,
} from "../components"
import Contact from "../components/blocks/Contact"
import SEO from "../components/Seo"
import { useTranslations } from "../utils/intl"

const Privacy: React.FC = () => {
  const { it } = useTranslations()
  return (
    <Wrapper>
      <Layout>
        <SEO title={it(["Privacy policy", "Privacy policy"])} />
        <PrivacyPolicy />
        <Contact css={{ marginTop: toEm(128), marginBottom: toEm(128) }} />
      </Layout>
    </Wrapper>
  )
}

const List = styled.ul({
  paddingLeft: toEm(16),
})
const Item = styled.li({
  fontWeight: 500,
  marginBottom: shevy.baseSpacing(0.5),
  "::before": {
    height: "0.8em",
    width: "0.8em",
    display: "inline-block",
    position: "relative",
    left: toEm(-16),
    color: "red",
    fill: "red",
    stroke: "red",
  },
})

const PrivacyPolicy: React.FC = () => {
  const { it } = useTranslations()

  return (
    <ContentWrapper
      css={{
        [`${H1}`]: { fontSize: toEm(60), fontWeight: 600, letterSpacing: -2 },
        [`${H2}`]: { fontSize: toEm(30) },
        [`${P}`]: { whiteSpace: "pre-line", maxWidth: toEm(837) },
        [`${List}`]: { marginBottom: shevy.baseSpacing(2) },
      }}
    >
      <img
        css={{
          width: "100%",
          marginBottom: shevy.baseSpacing(3),
          marginTop: shevy.baseSpacing(3),
        }}
        src={require("../images/walking-girl.svg").default}
      />

      <div>
        <H1>Privacy Policy</H1>
        <P>
          This Privacy Policy governs the manner in which we collect, use,
          maintain, and disclose information collected from users of our fitness
          app. This policy applies to the app and all services offered by us,
          both in educational settings and private use.
        </P>
        <H2>Acceptance of this Policy</H2>
        <P>
          By accessing and using our app, you signify acceptance of this policy.
          If you do not agree to this policy, please do not use our app. Your
          continued use of the app following the posting of changes to this
          policy will be deemed your acceptance of those changes.
        </P>
        <H2>Contact Information</H2>
        <P>
          For any inquiries, concerns, or requests regarding this policy or the
          processing of your personal data, please contact our Data Protection
          Officer at: <a href="mailto:anders@runbit.se">anders@runbit.se</a>.
        </P>
        <H2>Types of Data Collected</H2>
        <P>
          We collect various types of personal data to facilitate your use of
          our app and enhance your experience. The data we collect includes, but
          is not limited to:
        </P>
        <List>
          <Item>
            Email and password for account authentication and management.
          </Item>
          <Item>
            Location data to enhance service functionalities and improve user
            experience. This data is shared anonymously with our third-party
            service provider, Mapbox, to provide mapping services.
          </Item>
          <Item>
            Usage data and analytics to improve app functionality and user
            interface.
          </Item>
          <Item>
            Device identification data for security purposes and to prevent
            fraudulent use of our services.
          </Item>
        </List>
        <H2>Purposes for Processing Personal Data</H2>
        <P>Your data is processed for the following purposes:</P>
        <List>
          <Item>To provide, maintain, and improve our services.</Item>
          <Item>
            To manage your account and authenticate your access to our services.
          </Item>
          <Item>
            To communicate with you about your account or services we offer.
          </Item>
          <Item>
            To comply with legal obligations, resolve disputes, and enforce our
            agreements.
          </Item>
        </List>
        <H2>Legal Basis for Processing</H2>
        <P>
          We process your personal data on several legal bases, including your
          consent, the necessity to fulfill our contractual obligations to you,
          compliance with our legal obligations, and our legitimate interests in
          operating our business.
        </P>
        <H2>Disclosure of Personal Data</H2>
        <P>
          We may share your personal data with third parties only in the
          contexts outlined below:
        </P>
        <List>
          <Item>
            Service providers: Trusted third parties who perform services on our
            behalf, including hosting, data analysis, information technology,
            and related infrastructure, customer service, email delivery, and
            auditing services.
          </Item>
          <Item>
            Law enforcement and legal requests: We may disclose your data where
            required by law or subpoena or if we reasonably believe that such
            action is necessary to comply with the law and the reasonable
            requests of law enforcement or to protect the security or integrity
            of our service.
          </Item>
        </List>
        <H2>Mapbox and Location Data</H2>
        <P>
          We share anonymized location data with Mapbox to provide map-based
          service features. For more information on Mapbox's data practices,
          please refer to the Mapbox Privacy Policy available on their website.
        </P>
        <H2>Data Security</H2>
        <P>
          We employ a variety of security measures designed to protect your
          information from unauthorized access, alteration, or destruction. We
          maintain administrative, technical, and physical safeguards designed
          to protect the personal data you provide.
        </P>
        <H2>Data Retention and Deletion</H2>
        <P>
          Personal data is retained only for as long as necessary to fulfill the
          purposes for which it was collected, including as required or
          permitted by law. When no longer necessary, we securely delete or
          anonymize your personal data.
        </P>
        <H2>Your Rights</H2>
        <P>
          You have the right to access, correct, update, or request deletion of
          your personal data. You can also object to processing of your personal
          data, ask us to restrict processing of your personal data, or request
          portability of your personal data. If we have collected and processed
          your personal data with your consent, you can withdraw your consent at
          any time.
        </P>
      </div>
    </ContentWrapper>
  )
}

export default Privacy
